import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, displayNombre, imageUrl } from '../services/api';
import { SITE } from '../config/site';
import Hero from '../components/Hero';
import CollectionCard from '../components/CollectionCard';
import ProductCard from '../components/ProductCard';
import ProductModal from '../components/ProductModal';

const ACCENTS = ['#E91E96', '#7C3AED', '#DB2777', '#4338CA', '#C026D3', '#BE185D', '#6366F1'];

/** Elige portada única por categoría sin repetir la misma imagen. */
function buildCollectionCovers(byCategory) {
  const used = new Set();
  return SITE.collections.map((c, index) => {
    const list = byCategory[c.category] || [];
    const pick =
      list.find((p) => p.imagen && !used.has(p.imagen)) ||
      list.find((p) => p.imagen) ||
      null;
    if (pick?.imagen) used.add(pick.imagen);
    return {
      ...c,
      cover: pick,
      accent: ACCENTS[index % ACCENTS.length],
      featured: index === 0,
    };
  });
}

/** Destacados variados: prioriza categorías distintas. */
function diversifyProducts(products, limit = 8) {
  const byCat = new Map();
  for (const p of products) {
    if (!p.imagen) continue;
    const key = p.subcategoria || 'otro';
    if (!byCat.has(key)) byCat.set(key, []);
    byCat.get(key).push(p);
  }
  const picked = [];
  const keys = [...byCat.keys()];
  let i = 0;
  while (picked.length < limit && keys.length) {
    const key = keys[i % keys.length];
    const bucket = byCat.get(key);
    if (bucket?.length) picked.push(bucket.shift());
    else keys.splice(i % keys.length, 1);
    i += 1;
  }
  return picked;
}

function ProductSkeleton() {
  return (
    <div className="shop-card skeleton">
      <div className="shop-card-media">
        <div className="skeleton-img" />
      </div>
      <div className="skeleton-line" />
      <div className="skeleton-line short" />
    </div>
  );
}

export default function Home({ onAdd, onToast }) {
  const [featured, setFeatured] = useState([]);
  const [coversByCat, setCoversByCat] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const onInv = (ev) => {
      const detail = ev.detail || {};
      const domains = detail.domains || [];
      if (!domains.includes('product') && !domains.includes('inventory')) return;
      if (detail.reason === 'producto_eliminado' && detail.codigo) {
        const codigo = String(detail.codigo);
        setFeatured((prev) => prev.filter((p) => String(p.codigo) !== codigo));
      }
      setRefreshTick((n) => n + 1);
    };
    window.addEventListener('oh:invalidate', onInv);
    return () => window.removeEventListener('oh:invalidate', onInv);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    const featuredPromise = api.getProductos({ limit: 80 });
    const coverPromises = SITE.collections.map((c) =>
      api
        .getProductos({ subcategoria: c.category, limit: 16 })
        .then((r) => [c.category, r.data || []])
        .catch(() => [c.category, []])
    );

    Promise.all([featuredPromise, Promise.all(coverPromises)])
      .then(([featRes, coverPairs]) => {
        if (cancelled) return;
        setFeatured(featRes.data || []);
        setCoversByCat(Object.fromEntries(coverPairs));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  const collections = useMemo(() => buildCollectionCovers(coversByCat), [coversByCat]);
  const destacados = useMemo(() => diversifyProducts(featured, 8), [featured]);
  const promos = useMemo(() => diversifyProducts(featured, 2).slice(0, 2), [featured]);

  return (
    <>
      {error ? (
        <div className="api-error">
          <strong>{error}</strong>
          <span>
            Ejecuta en otra terminal: <code>cd backend &amp;&amp; npm start</code>
          </span>
        </div>
      ) : null}

      <Hero featuredProducts={featured} />

      <section className="home-strip">
        <div className="home-strip-inner">
          <div>
            <strong>+1.400</strong>
            <span>Productos</span>
          </div>
          <div>
            <strong>INVIMA</strong>
            <span>Registro sanitario</span>
          </div>
          <div>
            <strong>Medellín</strong>
            <span>Envíos nacionales</span>
          </div>
          <div>
            <strong>WhatsApp</strong>
            <span>Pedidos rápidos</span>
          </div>
        </div>
      </section>

      <section className="section" id="nosotros">
        <div className="section-head centered">
          <p className="section-kicker">Categorías</p>
          <h2>Encuentra lo Mejor para Ti</h2>
          <p>Colecciones reales del catálogo — cada una con su propia imagen</p>
        </div>
        <div className="collections-grid">
          {collections.map((c) => (
            <CollectionCard
              key={c.category}
              label={c.label}
              category={c.category}
              coverProduct={c.cover}
              accent={c.accent}
              featured={c.featured}
              href={c.href}
            />
          ))}
        </div>
      </section>

      <section className="section story-band">
        <div className="story-band-inner">
          <p className="section-kicker">Marca</p>
          <h2>Belleza que se ve y se siente</h2>
          <p>
            Importamos y distribuimos productos con registro INVIMA para profesionales y amantes
            del cuidado personal. Explora, arma tu pedido y te acompañamos por WhatsApp.
          </p>
          <div className="story-band-actions">
            <Link to="/catalogo" className="btn-primary">
              Ir al catálogo
            </Link>
            <a href={`https://wa.me/${SITE.whatsapp}`} target="_blank" rel="noreferrer" className="btn-ghost">
              Pedir asesoría
            </a>
          </div>
        </div>
      </section>

      {promos.length >= 2 ? (
        <section className="section promo-duo">
          {promos.map((p, i) => {
            const img = imageUrl(p.imagen);
            const name = displayNombre(p) || `Producto ${p.codigo}`;
            return (
              <Link
                key={`${p.codigo}-${i}`}
                to={`/catalogo?cat=${encodeURIComponent(p.subcategoria || '')}`}
                className="promo-card"
                style={
                  img
                    ? {
                        backgroundImage: `linear-gradient(135deg, rgba(233,30,150,.78), rgba(67,56,202,.72)), url(${img})`,
                      }
                    : undefined
                }
              >
                <span>{i === 0 ? 'Novedad' : 'Destacado'}</span>
                <h3>{name}</h3>
                <em>Ver en catálogo →</em>
              </Link>
            );
          })}
        </section>
      ) : null}

      <section className="section section-soft">
        <div className="section-head">
          <div>
            <p className="section-kicker">Catálogo</p>
            <h2>Productos destacados</h2>
          </div>
          <Link to="/catalogo" className="section-link">
            Ver todo el catálogo →
          </Link>
        </div>

        <div className="shop-grid">
          {loading
            ? Array.from({ length: 8 }).map((_, i) => <ProductSkeleton key={i} />)
            : destacados.map((p) => (
                <ProductCard
                  key={p.codigo}
                  product={p}
                  onPreview={setPreview}
                  onAdd={(prod) => {
                    onAdd(prod);
                  }}
                />
              ))}
        </div>
      </section>

      <section className="section social-cta">
        <div className="social-cta-inner">
          <div>
            <p className="section-kicker">Síguenos</p>
            <h2>@ohbellezamedellin</h2>
            <p>Novedades, tips de belleza y promociones en nuestras redes.</p>
          </div>
          <div className="social-cta-btns">
            <a href={SITE.social.instagram} target="_blank" rel="noreferrer">
              Instagram
            </a>
            <a href={SITE.social.facebook} target="_blank" rel="noreferrer">
              Facebook
            </a>
            <a href={`https://wa.me/${SITE.whatsapp}`} target="_blank" rel="noreferrer">
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      {preview ? (
        <ProductModal
          product={preview}
          products={destacados}
          onSelect={setPreview}
          onClose={() => setPreview(null)}
          onAdd={(prod, opts) => {
            onAdd(prod, opts);
          }}
        />
      ) : null}
    </>
  );
}
